import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { Session } from '../src/types';
import { addClient, errorText } from './api';
import type { ShopConfig } from './api';

const ZONES: [string, string][] = [
  ['America/New_York', 'Eastern'],
  ['America/Chicago', 'Central'],
  ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific'],
  ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'],
];
const here = (() => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return ZONES.some(([z]) => z === tz) ? tz : 'America/New_York';
  } catch {
    return 'America/New_York';
  }
})();
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Add a contractor: a new shop, still setting up. The number, 10DLC and logins come after. */
export function AddShop({ session, onClose, onAdded }: {
  session: Session;
  onClose: () => void;
  onAdded: (c: ShopConfig) => void;
}) {
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  const [city, setCity] = useState('');
  const [timezone, setTimezone] = useState(here);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const first = useRef<HTMLInputElement>(null);
  // Typing again clears the last problem.
  const edit = (set: (v: string) => void) => (e: { target: { value: string } }) => { set(e.target.value); setErr(''); };

  useEffect(() => {
    first.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [busy, onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!businessName.trim()) { setErr('Type the business name.'); return; }
    if (notifyEmail.trim() && !EMAIL.test(notifyEmail.trim())) { setErr('That alert email does not look right.'); return; }
    setBusy(true);
    setErr('');
    try {
      onAdded(await addClient(session, {
        businessName: businessName.trim(),
        timezone,
        ...(industry.trim() ? { industry: industry.trim() } : {}),
        ...(city.trim() ? { city: city.trim() } : {}),
        ...(notifyEmail.trim() ? { notifyEmail: notifyEmail.trim() } : {}),
      }));
    } catch (ex) {
      setErr(errorText(ex));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form className="sheet-panel" role="dialog" aria-modal="true" aria-labelledby="add-title" onSubmit={(e) => void submit(e)} noValidate>
        <h2 id="add-title">Add contractor</h2>
        <p className="fine">They start as setting up. Missed calls are texted once the shop has a business number, so add the number after their own 10DLC campaign is approved.</p>
        <label>Business name<input ref={first} value={businessName} onChange={edit(setBusinessName)} maxLength={80} placeholder="Kowalski Roofing" /></label>
        <div className="pair">
          <label>Trade<input value={industry} onChange={edit(setIndustry)} list="trades-new" maxLength={60} placeholder="Roofing" /></label>
          <label>Town<input value={city} onChange={edit(setCity)} maxLength={60} placeholder="Novi" /></label>
        </div>
        <datalist id="trades-new">
          {['Roofing', 'Remodeling', 'Painting', 'Concrete', 'Gutters', 'Siding', 'Windows', 'Plumbing', 'HVAC', 'Electrical', 'Landscaping'].map((t) => <option key={t} value={t} />)}
        </datalist>
        <label>
          Time zone
          <select value={timezone} onChange={edit(setTimezone)}>
            {ZONES.map(([z, name]) => <option key={z} value={z}>{name}</option>)}
          </select>
        </label>
        <label>
          Lead alerts email <span className="optional">optional</span>
          <input type="email" value={notifyEmail} onChange={edit(setNotifyEmail)} placeholder="mike@kowalskiroofing.com" />
        </label>
        {err && <p className="err" role="alert">{err}</p>}
        <div className="actions">
          <button className="primary" type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add contractor'}</button>
          <button className="ghost" type="button" disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
